'use client'

/**
 * Files on a hand-off ask bar (#16102) — Today, the prep lens, the org page
 * and ActionBoard.
 *
 * ## Why a bar asks whether the panel is mounted
 *
 * Every one of those bars hands its question to the docked AI Chat panel, and
 * the files go with it (`useAskInChatHandoff`). So the question "may this bar
 * take files?" is exactly "is there a panel to receive them?". Files are part
 * of AI chat for every org (#16724), so the panel takes them whenever
 * `AiChatWrapper` renders `AiChatRuntime`; the runtime registers itself here,
 * and a bar reads it.
 *
 * The runtime is a DOM sibling of the application pane (it sits in the
 * frame's sidecar), so no React context can carry the answer down to a page —
 * the reason `AiChatRuntime` pushes `activateChat` into an ancestor provider.
 * This is the same push, into a tiny external store, so the 1,000-line
 * provider does not grow for one boolean.
 *
 * Registration is ref-counted and undone on unmount, so anything that
 * unmounts the runtime (signing out, a failed wrapper render) takes the
 * attach buttons with it. The server snapshot is `false`: a bar never
 * server-renders an attach button that hydration would then have to remove.
 *
 * Home is not a caller: it answers in the page and stages files there
 * (#16101).
 */

import { useEffect, useSyncExternalStore } from 'react'

import {
  type ChatAttachments,
  useChatAttachments,
} from '@/components/ai-chat/use-chat-attachments'
import type { ChatAttachmentSurface } from '@/lib/ai-chat/chat-attachments'

let enabledRegistrations = 0
const listeners = new Set<() => void>()

function emit() {
  for (const listener of listeners) listener()
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

const readEnabled = () => enabledRegistrations > 0
const readServerEnabled = () => false

/** Called by `AiChatRuntime`, for as long as the docked panel is mounted. */
export function useRegisterAskBarAttachments() {
  useEffect(() => {
    enabledRegistrations += 1
    emit()
    return () => {
      enabledRegistrations -= 1
      emit()
    }
  }, [])
}

/** Whether the mounted panel takes files, so a hand-off bar may too. */
export function useAskBarAttachmentsAvailable(): boolean {
  return useSyncExternalStore(subscribe, readEnabled, readServerEnabled)
}

/**
 * A hand-off bar's `attachments` slot: the shared staging state, or
 * `undefined` (no attach button, no paste) while the panel takes no files.
 */
export function useAskBarAttachments(
  surface: ChatAttachmentSurface,
): ChatAttachments | undefined {
  const files = useChatAttachments({ surface })
  return useAskBarAttachmentsAvailable() ? files : undefined
}
